import { useEffect, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  InspectEntityDialog,
  UnregisterEntityDialog,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';

import { getThemeTokens } from '../../../theme/tokens';

type InspectTab = 'overview' | 'ancestry' | 'colocated' | 'json' | 'yaml';

/**
 * The three-dot entity actions menu (Inspect / Unregister). The bespoke
 * {@link EntityPageShell} replaces Backstage's stock `EntityLayout`, which is
 * where this menu normally lives, so it is reimplemented here against the app's
 * MUI v5 theme. Unregister is gated by the catalog delete permission, matching
 * upstream behaviour.
 */
export const EntityHeaderContextMenu = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { entity } = useEntity();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unregisterOpen, setUnregisterOpen] = useState(false);

  const unregisterPermission = useEntityPermission(
    catalogEntityDeletePermission,
  );

  const inspectTab = searchParams.get('inspect');
  const inspectOpen = typeof inspectTab === 'string';

  // Close everything when navigating to a different entity.
  useEffect(() => {
    setAnchorEl(null);
    setUnregisterOpen(false);
  }, [location.pathname]);

  const openMenu = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const setInspectParam = (value: string | null) =>
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (value === null) {
        params.delete('inspect');
      } else {
        params.set('inspect', value);
      }
      return params;
    });

  const onInspect = () => {
    closeMenu();
    setInspectParam('');
  };

  const onUnregister = () => {
    closeMenu();
    setUnregisterOpen(true);
  };

  const onUnregisterConfirmed = () => {
    setUnregisterOpen(false);
    navigate('/catalog');
  };

  return (
    <>
      <Tooltip title="More options">
        <IconButton
          aria-label="More options"
          onClick={openMenu}
          size="small"
          sx={{
            color: tokens.textMuted,
            '&:hover': { color: tokens.textPrimary },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={onInspect}>
          <ListItemIcon>
            <BugReportIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Inspect entity</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={onUnregister}
          disabled={!unregisterPermission.allowed}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Unregister entity</ListItemText>
        </MenuItem>
      </Menu>

      {inspectOpen ? (
        <InspectEntityDialog
          open
          entity={entity}
          initialTab={inspectTab ? (inspectTab as InspectTab) : undefined}
          onClose={() => setInspectParam(null)}
          onSelect={tab => setInspectParam(tab)}
        />
      ) : null}

      <UnregisterEntityDialog
        open={unregisterOpen}
        entity={entity}
        onConfirm={onUnregisterConfirmed}
        onClose={() => setUnregisterOpen(false)}
      />
    </>
  );
};
